import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Dialog, Flex, Text, TextField } from '@radix-ui/themes'
import { CheckIcon, CopyIcon } from '@radix-ui/react-icons'
import { OPS_SIGNED_URL_TTL_S } from '@willie/supabase/storage'
import { useAuth } from '~/auth/AuthProvider'
import { createShareableImageLink } from '~/features/installations/service'

const LIFETIME_MS = OPS_SIGNED_URL_TTL_S.clientShare * 1000

const formatExpiry = (date: Date): string =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

interface ShareImageLinkDialogProps {
  path: string
  trigger: ReactNode
}

/**
 * Mints a customer-shareable link for one stored image.
 *
 * The link is cached for most of its lifetime so reopening the dialog reuses it
 * rather than minting another live capability for the same object. Copying is a
 * direct click on an already-resolved URL — Safari drops a clipboard write that has
 * awaited anything since the user's gesture.
 */
export const ShareImageLinkDialog = ({ path, trigger }: ShareImageLinkDialogProps) => {
  const { supabaseClient } = useAuth()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const {
    data: link,
    isPending,
    error
  } = useQuery({
    queryKey: ['ops-share-link', path] as const,
    queryFn: () => createShareableImageLink(supabaseClient, path),
    enabled: open,
    staleTime: LIFETIME_MS * 0.8,
    gcTime: LIFETIME_MS
  })

  const copy = async () => {
    if (!link) return
    await navigator.clipboard.writeText(link.url)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setCopied(false)
      }}
    >
      <Dialog.Trigger>{trigger}</Dialog.Trigger>
      <Dialog.Content maxWidth="520px">
        <Dialog.Title size="3">Share with a customer</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="3">
          Anyone with this link can view the image until it expires. It cannot be revoked early.
        </Dialog.Description>

        {error ? (
          <Text size="2" color="red">
            Could not create a link. Refresh the page to retry.
          </Text>
        ) : isPending || !link ? (
          <Text size="2" color="gray">
            Creating link…
          </Text>
        ) : (
          <Flex direction="column" gap="2">
            <Flex gap="2" align="center">
              <TextField.Root size="2" value={link.url} readOnly className="flex-1" />
              <Button size="2" variant="soft" onClick={copy}>
                {copied ? <CheckIcon /> : <CopyIcon />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Flex>
            <Text size="1" color="gray">
              Expires {formatExpiry(link.expiresAt)}
            </Text>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}
